'use client'

import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { createContext, useContext, useMemo, useRef, useState, type MutableRefObject, type PropsWithChildren } from 'react'
import { type SnackbarProps } from './Snackbar'

type QueueItem = () => void

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface SnackbarContextProps<Props extends SnackbarProps = any> {
  snackbarProps: Props
  handleExited: () => void
  openSnackbar: (props: Props) => void
  closeSnackbar: () => void
  /**
   * @internal
   */
  _queue: MutableRefObject<QueueItem[]>
  /**
   * @internal
   */
  _isOpening: MutableRefObject<boolean>
}

export const SnackbarContext = createContext<SnackbarContextProps | null>(null)

export const SnackbarProvider = (props: PropsWithChildren) => {
  const { children } = props
  const [snackbarProps, setSnackbarProps] = useState<SnackbarProps>({
    open: false,
  })
  const parentContext = useContext(SnackbarContext)
  const localeQueue = useRef<QueueItem[]>([])
  const localeIsOpening = useRef(false)
  const queue = parentContext?._queue ?? localeQueue
  const isOpening = parentContext?._isOpening ?? localeIsOpening

  const dequeue = useCallbackEvent(() => {
    if (isOpening.current) return
    const updateState = queue.current.shift()
    if (!updateState) return
    isOpening.current = true
    updateState()
  })

  const openSnackbar = useCallbackEvent((props: SnackbarProps) => {
    queue.current.push(() => {
      setSnackbarProps({
        ...props,
        open: true,
      })
    })

    dequeue()
  })

  const closeSnackbar = useCallbackEvent(() => {
    // use callback to avoid open is always true (cause by react batch update).
    setSnackbarProps(
      value => ({
        ...value,
        open: false,
      }),
    )
  })

  const handleExited = useCallbackEvent(() => {
    isOpening.current = false
    dequeue()
  })

  const contextValue = useMemo(() => ({
    handleExited,
    openSnackbar,
    closeSnackbar,
    snackbarProps,
    _queue: queue,
    _isOpening: isOpening,
  }), [closeSnackbar, handleExited, isOpening, openSnackbar, queue, snackbarProps])

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
    </SnackbarContext.Provider>
  )
}
